import React, { useCallback, useState } from "react";
import { DragDropContext, Draggable, Droppable, DropResult } from "@hello-pangea/dnd";
import AttributeSortingItem from "./AttributeSortingItem";
import Utils from "Common/Utils";
import Button from "Components/DesignSystem/Button/Button";
import { Drag_n_drop } from "Components/DesignSystem/Iconography/Icons";

type AttributeOption = {
    id: string;
    name: string;
};

type AttributeSortingListProps = {
    iteration: Iteration;
    attributeOptions: AttributeOption[];
    onSortingChange: (iterationId: string, attributeSorting: AttributeSortItem[]) => void;
};

const AttributeSortingList: React.FC<AttributeSortingListProps> = ({
    iteration,
    attributeOptions,
    onSortingChange,
}) => {
    const [items, setItems] = useState<AttributeSortItem[]>(iteration.attribute_sorting || []);

    const handleAddSorting = useCallback(() => {
        const newItem: AttributeSortItem = {
            id: Utils.generate_guid(),
            attribute_name: '',
            attribute_id: '',
            order: 'ASC',
            priority: items.length + 1,
        };

        const updatedItems = [...items, newItem];
        setItems(updatedItems);
        onSortingChange(iteration.id, updatedItems);
    }, [items, iteration.id, onSortingChange]);

    const handleDeleteItem = useCallback((id: string) => {
        const updatedItems = items
            .filter(item => item.id !== id)
            .map((item, index) => ({ ...item, priority: index + 1 }));

        setItems(updatedItems);
        onSortingChange(iteration.id, updatedItems);
    }, [items, iteration.id, onSortingChange]);

    const handleAttributeChange = useCallback((id: string, attributeId: string, attributeName: string) => {
        const updatedItems = items.map(item =>
            item.id === id ? { ...item, attribute_id: attributeId, attribute_name: attributeName } : item
        );

        setItems(updatedItems);
        onSortingChange(iteration.id, updatedItems);
    }, [items, iteration.id, onSortingChange]);

    const handleOrderChange = useCallback((id: string, order: 'ASC' | 'DESC') => {
        const updatedItems = items.map(item =>
            item.id === id ? { ...item, order } : item
        );

        setItems(updatedItems);
        onSortingChange(iteration.id, updatedItems);
    }, [items, iteration.id, onSortingChange]);

    const handleDragEnd = useCallback((result: DropResult) => {
        // Dropped outside the list
        if (!result.destination) {
            return;
        }

        const sourceIndex = result.source.index;
        const destinationIndex = result.destination.index;

        // If position didn't change
        if (sourceIndex === destinationIndex) {
            return;
        }

        const updatedItems = Array.from(items);
        const [removed] = updatedItems.splice(sourceIndex, 1);
        updatedItems.splice(destinationIndex, 0, removed);

        // Update priorities after reordering
        const reorderedItems = updatedItems.map((item, index) => ({
            ...item,
            priority: index + 1,
        }));

        setItems(reorderedItems);
        onSortingChange(iteration.id, reorderedItems);
    }, [items, iteration.id, onSortingChange]);

    return (
        <div className="tw-w-full" data-testid="attribute-sorting-list">
            <div className="tw-justify-start tw-text-ink-secondary tw-text-sm tw-font-medium tw-ps-2.5 tw-py-4">Iteration {iteration.name}</div>
            <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId={`attribute-sorting-${iteration.id}`}>
                    {(provided) => (
                        <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            className="tw-w-full tw-mb-4 tw-space-y-4"
                        >
                            {items.map((item, index) => (
                                <Draggable
                                    key={item.id}
                                    draggableId={item.id}
                                    index={index}
                                >
                                    {(provided, snapshot) => (
                                        <div
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            {...provided.dragHandleProps}
                                            className={`tw-row tw-flex tw-items-center tw-pe-2.5 ${snapshot.isDragging ? 'tw-opacity-70' : 'tw-opacity-100'}`}
                                        >
                                            <div className="tw-px-2 tw-text-ink-tertiary tw-flex"><Drag_n_drop /></div>
                                            <AttributeSortingItem
                                                item={item}
                                                index={index}
                                                attributeOptions={attributeOptions}
                                                allSelectedItems={items}
                                                onDelete={handleDeleteItem}
                                                onAttributeChange={handleAttributeChange}
                                                onOrderChange={handleOrderChange}
                                            />
                                        </div>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>

            {/* § 4.5 grammar: the bottom add is a DS secondary (the baked-#091CAA
                outline and its never-compiled blue-50 hover are gone). */}
            <div className="tw-ml-6 tw-mt-6">
                <Button
                    variant="secondary"
                    onClick={handleAddSorting}
                    data-testid="add-sorting-button"
                >
                    Add Sorting
                </Button>
            </div>
        </div>
    );
};

export default AttributeSortingList;